import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import ErrorBoundary from './components/ErrorBoundary';
import ScrollToTop from './components/ScrollToTop';
import { useAuth } from './contexts/AuthContext';

// Lazy load pages
const Login = lazy(() => import('./pages/login'));
const ForgotPassword = lazy(() => import('./pages/forgot-password'));
const CarrierDashboard = lazy(() => import('./pages/carrier-dashboard'));
const DriverManagement = lazy(() => import('./pages/driver-management'));
const FleetManagement = lazy(() => import('./pages/fleet-management'));
const LoadsManagement = lazy(() => import('./pages/loads-management'));
const LoadDetails = lazy(() => import('./pages/load-details'));
const TripPlanning = lazy(() => import('./pages/trip-planning'));
const CarbonDashboard = lazy(() => import('./pages/carbon-dashboard'));
const ShipperPortal = lazy(() => import('./pages/shipper-portal'));
const SettingsProfile = lazy(() => import('./pages/settings-profile'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Loading component
const LoadingSpinner = () => (
  <div className="flex items-center justify-center h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
  </div>
);

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public Route wrapper (redirect to dashboard if authenticated)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (user) {
    return <Navigate to="/carrier-dashboard" replace />;
  }

  return children;
};

export default function Routes() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <Suspense fallback={<LoadingSpinner />}>
          <RouterRoutes>
            {/* Public routes */}
            <Route 
              path="/login" 
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              } 
            />
            <Route 
              path="/forgot-password" 
              element={
                <PublicRoute>
                  <ForgotPassword />
                </PublicRoute>
              } 
            />

            {/* Protected routes */}
            <Route 
              path="/carrier-dashboard" 
              element={
                <ProtectedRoute>
                  <CarrierDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/driver-management" 
              element={
                <ProtectedRoute>
                  <DriverManagement />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/fleet-management" 
              element={
                <ProtectedRoute>
                  <FleetManagement />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/loads-management" 
              element={
                <ProtectedRoute>
                  <LoadsManagement />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/load-details" 
              element={
                <ProtectedRoute>
                  <LoadDetails />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/trip-planning" 
              element={
                <ProtectedRoute>
                  <TripPlanning />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/carbon-dashboard" 
              element={
                <ProtectedRoute>
                  <CarbonDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/shipper-portal" 
              element={
                <ProtectedRoute>
                  <ShipperPortal />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/settings-profile" 
              element={
                <ProtectedRoute>
                  <SettingsProfile />
                </ProtectedRoute>
              } 
            />

            {/* Default and 404 routes */}
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="*" element={<NotFound />} />
          </RouterRoutes>
        </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
}